import React, {
  forwardRef,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { FieldTypes, parseSelectOptions } from "utils/formUtils";
import message from "components/CustomMessage";
import GenericMappingForm from "../GenericMappingForm";
import { Form } from "antd";

const BitSightMappingForm = forwardRef(function BitSightMappingForm(
  props,
  ref
) {
  const [form] = Form.useForm();
  const [companyList, setCompanyList] = useState(() => {
    if (props.record) {
      const { companyId, companyName } = props.record;
      return [
        {
          id: companyId,
          name: companyName,
        },
      ];
    }
    return [];
  });
  const [selectedCompanyID, setSelectedCompanyID] = useState(() => {
    if (props.record) {
      const { companyId } = props.record;
      return companyId;
    }
  });
  const [subsidiaryList, setSubsidiaryList] = useState(() => {
    if (props.record) {
      const { companyId, subsidiaryIds, subsidiaryNames } = props.record;
      const subsidiaryIdArray = subsidiaryIds.split(",") || [];
      const subsidiaryNameArray = subsidiaryNames.split(",") || [];
      return {
        [companyId]: subsidiaryIdArray.map((id, index) => ({
          id,
          name: subsidiaryNameArray[index],
        })),
      };
    }
    return {};
  });

  useEffect(() => {
    const { promise, cancel } =
      ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, {
        login_id: props.loginConfigId,
      });
    promise
      .then((response) => {
        const { projects } = response;
        setCompanyList(projects);
      })
      .catch((err) => {
        message.error("Problem fetching companies");
      });

    return cancel;
  }, [props.loginConfigId, props.toolName]);

  const fetchSubsidiaries = useCallback(
    (companyId) => {
      const { promise, cancel } =
        ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, {
          login_id: props.loginConfigId,
          project: companyId,
        });

      promise
        .then((response) => {
          const { projects } = response;
          setSubsidiaryList((subsidiaryList) => ({
            ...subsidiaryList,
            [companyId]: projects,
          }));
        })
        .catch(() => {
          message.error("Problem fetching subsidiaries");
        });

      return cancel;
    },
    [props.loginConfigId, props.toolName]
  );

  useEffect(
    () => fetchSubsidiaries(selectedCompanyID),
    [selectedCompanyID, fetchSubsidiaries]
  );

  const getExtraFieldsData = useCallback(
    (config) => {
      const { companyId, subsidiaryIds, tags } = config;
      const companyName = companyList.find(
        (company) => company.id === companyId
      )?.name;
      const data = subsidiaryList[companyId]?.reduce(
        (acc, cv) => {
          const { id, name } = cv;
          if (subsidiaryIds.includes(id)) {
            acc.id.push(id);
            acc.name.push(name);
          }
          return acc;
        },
        { id: [], name: [] }
      );

      return {
        companyId,
        companyName,
        subsidiaryIds: data.id.join(),
        subsidiaryNames: data.name.join(),
        tags,
      };
    },
    [companyList, subsidiaryList]
  );

  const onCompanyChange = useCallback(
    (value, field) => {
      setSelectedCompanyID(value);
      const { name } = field;
      const configurations = form.getFieldValue("configurations") || [];
      let updatedConfiguration = [...configurations];
      updatedConfiguration[name] = {
        ...updatedConfiguration[name],
        subsidiaryIds: undefined,
      };
      form.setFieldsValue({ configurations: updatedConfiguration });
    },
    [form]
  );

  const parseExtraFieldsDataFromResponse = useCallback((config) => {
    const { companyId, subsidiaryIds } = config;
    return {
      companyId,
      subsidiaryIds: subsidiaryIds.split(","),
    };
  }, []);

  const isProjectFieldDisabled = useCallback((fieldKey, form) => {
    const fieldConfiguration = form.getFieldValue("configurations")[fieldKey];
    return !fieldConfiguration || !fieldConfiguration.companyId;
  }, []);

  const extraFields = useMemo(
    () => [
      {
        name: "companyId",
        colSpan: 10,
        type: FieldTypes.SELECT,
        label: "Company",
        options: companyList.map((option) => {
          const { label = option.name, value = option.id } = option;
          return { label, value };
        }),
        required: true,
        props: {
          autoFocus: true,
          onChange: onCompanyChange,
        },
      },
      {
        name: "subsidiaryIds",
        colSpan: 12,
        type: FieldTypes.MULTI_SELECT,
        label: "Subsidiary",
        required: true,
        propsGenerator: (fieldKey) => ({
          disabled: isProjectFieldDisabled(fieldKey, form),
        }),
        props: {
          getFormListOptions: (fieldKey) => {
            const configs = form.getFieldValue("configurations");
            const companyId = configs[fieldKey]?.companyId;
            return companyId
              ? parseSelectOptions(subsidiaryList[companyId])
              : [];
          },
        },
      },
      {
        name: "tags",
        label: "Tags",
        NoDefaultLengthRule: true,
        help: "Specify case-sensitive tags, seperate with comma if multiple tags",
      },
    ],
    [companyList, onCompanyChange, subsidiaryList, isProjectFieldDisabled, form]
  );
  return (
    <GenericMappingForm
      ref={ref}
      {...props}
      form={form}
      getExtraFieldsData={getExtraFieldsData}
      parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
      extraFields={extraFields}
    />
  );
});

export default BitSightMappingForm;
